"use client";

import { StudySession, StudySessionProgress, StudySessionProgressEnum } from "@/graphql/schema";
import ProgressPill from "../ProgressPill";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import toast from "react-hot-toast";
import OutlineTextButton from "@/components/CircularButton/styled/OutlineTextButton";
import { getProgressValue } from "@/hooks/study/statsUtils";
import { borderRadius } from "@/utils/borderRadius";
import Skeleton from "@mui/material/Skeleton";
import styles from "./studyingProgressCard.module.css";
import { spacing } from "@/utils/spacing";

const StudyingProgressCard = ({
    studySession,
    flashcardSetId,
    total: _total,
    description,
    onStudy,
    isCommittingUpdates,
    withStats,
    ...divProps
}: {
    flashcardSetId: string;
    studySession: StudySession | null | undefined;
    total?: number;
    description?: React.ReactNode;
    onStudy?: (studyFrom: StudySessionProgressEnum) => void;
    isCommittingUpdates?: boolean;
    withStats?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => {
    // we pass total in case there is no study session, in that case everything is "new"
    const total = studySession
        ? Object.values(studySession.progress ?? {}).reduce((acc, curr) => acc + (curr ?? 0), 0)
        : (_total ?? 0);

    return (
        <FlexColumn
            {...divProps}
            style={{
                flexWrap: "wrap",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                padding: "3.2rem 2.5rem",
                ...divProps.style,
            }}
            as={"section"}>
            <span
                style={{ marginBottom: withStats ? spacing.XS : "2.8rem" }}
                data-testid="auto-id-fc1ed2c0-fa8d-4ee4-854c-b1eaddba7ec5">
                <FlexRowAlignCenter style={{ justifyContent: "space-between" }}>
                    <h2
                        style={{ fontSize: "1.8rem", fontWeight: "600" }}
                        data-testid="auto-id-df36be4e-7b69-4ed0-91ac-f55a66bbe7dc">Studying Progress</h2>
                    <div
                        style={{
                            padding: "0.6rem 1rem",
                            backgroundColor: themeColors.mcStudyLight1,
                            borderRadius: borderRadius.card,
                        }}
                        data-testid="auto-id-d6cbd9ae-acc8-4432-8c0b-e9d791613e90">
                        <span data-testid="auto-id-856748ff-7e0b-43fe-a906-89096b1bc183">
                            {Math.round(getProgressValue(studySession?.progress || ({} as StudySessionProgress)))}%
                        </span>
                    </div>
                </FlexRowAlignCenter>
                {description && <p
                    style={{ fontSize: "1.5rem", marginTop: "0.6rem" }}
                    data-testid="auto-id-8f959543-5d6f-4b77-b97c-aba037db02cb">{description}</p>}
            </span>
            <FlexColumn style={{ gap: "2.2rem", width: "100%" }}>
                {[
                    StudySessionProgressEnum.NEW,
                    StudySessionProgressEnum.LEARNING,
                    StudySessionProgressEnum.REVIEWING,
                    StudySessionProgressEnum.MASTERED,
                ].map((studyProgress, index) => {
                    const getValue = (): number => {
                        if (index === 0 && !studySession) {
                            return total as number;
                        } else if (isCommittingUpdates) {
                            return 0;
                        } else {
                            return studySession?.progress?.[studyProgress] || 0;
                        }
                    };

                    const getWidth = (): string => {
                        const cardPercentage = getValue() / total;
                        // 16rem is the min-width of the `ProgressPill`
                        // 2.3rem is approx `getValue()` text size
                        // 11rem is the gap + button besides it
                        return `calc(16rem + (100% - 16rem - 2.3rem - 11rem) * ${cardPercentage})`;
                    };

                    return (
                        <FlexRowAlignCenter key={studyProgress} style={{ justifyContent: "space-between" }}>
                            <FlexRowAlignCenter
                                className={styles.progressPill}
                                style={{ width: getWidth(), gap: "1.2rem", maxWidth: "100%" }}>
                                {isCommittingUpdates ? (
                                    <Skeleton
                                        sx={{ borderRadius: 999 }}
                                        animation="wave"
                                        variant="rounded"
                                        width={"100%"}
                                        height={"4rem"}
                                    />
                                ) : (
                                    <ProgressPill progress={studyProgress} style={{ width: "100%" }} />
                                )}
                                <span
                                    style={{ fontSize: "1.4rem" }}
                                    data-testid="auto-id-6daab575-2c2d-4408-a3bf-a8ec14c355fb">{isCommittingUpdates ? "" : getValue()}</span>
                            </FlexRowAlignCenter>
                            {!!onStudy && (
                                <OutlineTextButton
                                    sx={{
                                        fontSize: "1.4rem",
                                        height: "4rem",
                                        width: "9rem",
                                        backgroundColor: themeColors.neutralWhite,
                                        color: themeColors.neutralBlack,
                                        padding: "2rem",
                                    }}
                                    onClick={() => {
                                        if (getValue() === 0) {
                                            return toast.error("You have no cards to study in this bucket.");
                                        }
                                        onStudy(studyProgress);
                                    }}>
                                    Study
                                </OutlineTextButton>
                            )}
                        </FlexRowAlignCenter>
                    );
                })}
            </FlexColumn>
        </FlexColumn>
    );
};

export default StudyingProgressCard;
